import { useState, type FormEvent } from 'react'
import { cn } from '../utils'
import { Plus } from 'lucide-react'
import { motion } from 'framer-motion'

export type CreateOrgFormProps = {
  onSubmit: (name: string) => Promise<void>
  className?: string
}

export function CreateOrgForm({ onSubmit, className }: CreateOrgFormProps) {
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed || submitting) return
    setSubmitting(true)
    try {
      await onSubmit(trimmed)
      setName('')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <motion.div
      className={cn('rounded-lg border border-border bg-card p-6', className)}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      <h2 className="text-lg font-medium text-foreground mb-1">Create an Organization</h2>
      <p className="text-sm text-muted-foreground mb-5">
        Organization members share your map generation quota. Maps created on the org tab count toward the org pool.
      </p>
      <form onSubmit={handleSubmit} className="flex items-center gap-3">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Organization name"
          className="flex-1 rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary"
          disabled={submitting}
        />
        <motion.button
          type="submit"
          disabled={!name.trim() || submitting}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none transition-colors"
          whileTap={{ scale: 0.97 }}
        >
          <Plus className="h-4 w-4" />
          {submitting ? 'Creating...' : 'Create'}
        </motion.button>
      </form>
    </motion.div>
  )
}
